import { Alert } from "@gigpro/ui/Alert";
import { Edit } from "@gigpro/ui/Icons";
import { currencyMaskProps, MaskedInput } from "@gigpro/ui/MaskedInput";
import { cn } from "@gigpro/ui/tailwind/cn";
import { ToggleGroup, ToggleGroupItemUnstyled } from "@gigpro/ui/ToggleGroup";
import { formatCurrency } from "@gigpro/utils";
import { cva } from "class-variance-authority";
import React from "react";
import { track } from "utils/analytics";
import { wageOptionProps } from "../../wageChoice";

const BELOW_AVERAGE_WAGE_WARNING = "Wage is below avg; raise the wage to attract skilled pros";
const BELOW_RECOMMENDED_WAGE_WARNING = "Wage is below Recommended";

export type WageOption = {
  key: "recommended" | "avg" | "custom";
  wage: number;
};

const wageOptionLabelLookup: Record<WageOption["key"], string> = {
  recommended: "Recommended",
  avg: "Market Avg",
  custom: "Custom",
};

type Look = "boxes" | "segmented";

const groupVariants = cva("bg-transparent p-0", {
  variants: {
    look: {
      boxes: "grid max-w-80 gap-1",
      // The posting page's control size: one grey group of 30 px items, the picked one green.
      segmented: "flex w-fit max-w-full flex-wrap items-center gap-1 rounded-sm bg-background/50 p-1",
    },
  },
});

const optionVariants = cva("", {
  variants: {
    look: {
      boxes:
        "flex h-9 flex-col items-center justify-center rounded-sm border-2 border-medium-gray bg-white data-[state=on]:border-success data-[validation=error]:data-[state=on]:border-error",
      segmented:
        "flex h-6 items-center gap-1 rounded-sm px-3 text-normal font-semibold text-gray data-[state=on]:bg-brand data-[state=on]:text-white data-[validation=error]:data-[state=on]:bg-error",
    },
    custom: { true: "", false: "" },
  },
  compoundVariants: [
    {
      look: "boxes",
      custom: true,
      className:
        "flex-row gap-0 pr-2 data-[state=on]:border-success data-[state=on]:data-[validation=warning]:border-warning sm:gap-1 sm:pr-3",
    },
    { look: "segmented", custom: true, className: "pr-2 pl-0" },
  ],
});

export interface WagePickerProps extends Omit<
  React.ComponentProps<typeof ToggleGroup>,
  "defaultValue" | "value" | "onValueChange" | "type"
> {
  className?: string;

  value: WageOption | undefined;
  onValueChange: (value: WageOption) => void;

  defaultOption?: WageOption["key"];
  reverse?: boolean;

  templateWage?: number;
  /** The saved position's own wage, for analytics, where `templateWage` is not it (the posting page's is the current wage). */
  savedWage?: number | null;
  avgWage: number;
  recommendedWage: number;
  /** Two choices, a typed wage or Recommended; the warning keys off Recommended. */
  recommendationRefresh?: boolean;

  helperText?: string;
  error?: string;
  /** The custom wage field's accessible name; the field shows only a placeholder. */
  customLabel?: string;
  /** Nothing is picked until the manager picks: no option is chosen for them on mount. */
  noDefault?: boolean;
  /** With `noDefault`, the default option still shows as picked, as on the classic form, but is never reported as an answer. */
  showDefault?: boolean;
  /** The custom wage is reported once, when the field is left or Enter is pressed, not on every keystroke. */
  commitOnBlur?: boolean;
  look?: Look;
}

// Market Avg is gone with the refresh: a wage already set to it shows as the typed one.
const getPickedKey = (recommendationRefresh: boolean, value?: WageOption) =>
  recommendationRefresh && value?.key === "avg" ? "custom" : value?.key;

const getWarning = (wageBar: number, recommendationRefresh: boolean, value?: WageOption) => {
  const text = recommendationRefresh ? BELOW_RECOMMENDED_WAGE_WARNING : BELOW_AVERAGE_WAGE_WARNING;
  return value && value.wage < wageBar ? text : undefined;
};

export function WagePicker({
  className,
  value,
  templateWage,
  savedWage = templateWage,
  avgWage,
  recommendedWage,
  defaultOption = "custom",
  reverse,
  helperText,
  error,
  customLabel,
  noDefault = false,
  showDefault = false,
  commitOnBlur = false,
  look = "boxes",
  recommendationRefresh = false,
  onValueChange,
  ...props
}: Readonly<WagePickerProps>) {
  const [typed, setTyped] = React.useState(false);
  const pickedKey = getPickedKey(recommendationRefresh, value);
  const [customWage, setCustomWage] = React.useState(pickedKey === "custom" ? value?.wage : templateWage);
  // A wage set from outside (the chat) while nothing is being typed replaces what the field shows.
  const heldWage = value?.key === "custom" ? value.wage : undefined;
  if (commitOnBlur && !typed && heldWage !== undefined && heldWage !== customWage) setCustomWage(heldWage);
  const unorderedOptions: Array<WageOption> = [
    { key: "custom", wage: customWage ?? 0 },
    ...(recommendationRefresh ? [] : [{ key: "avg" as const, wage: avgWage }]),
    { key: "recommended", wage: recommendedWage },
  ];
  const options = reverse ? unorderedOptions.toReversed() : unorderedOptions;

  const selectDefault = React.useEffectEvent((key: typeof defaultOption) => {
    const defaultSelection = options.find((option) => option.key === key);
    if (defaultSelection && !value && !noDefault) onValueChange(defaultSelection);
  });
  React.useEffect(() => {
    selectDefault(defaultOption);
  }, [defaultOption]);

  // Only a person's own pick is counted: the default the picker selects on mount never comes through here.
  const trackPick = (option: WageOption) =>
    track(
      "Shift Posting - Wage Option Selected",
      wageOptionProps({ option: option.key, wage: option.wage, recommendedWage, savedWage }),
    );

  const onSelectWageOption = (key: WageOption["key"]) => {
    const option = options.find((option) => option.key === key);
    if (!option) return;
    if (key !== pickedKey) trackPick(option);
    onValueChange(option);
  };

  const wageBar = recommendationRefresh ? recommendedWage : avgWage;
  const warning = getWarning(wageBar, recommendationRefresh, value);

  const selectedKey = pickedKey ?? (noDefault && !showDefault ? "" : defaultOption);
  return (
    <ToggleGroup
      type="single"
      value={selectedKey}
      onValueChange={(value) => onSelectWageOption(value as WageOption["key"])}
      className={cn(
        groupVariants({ look }),
        look === "boxes" && (options.length === 2 ? "grid-cols-2" : "grid-cols-3"),
        className,
      )}
      data-testid="wage-picker"
      {...props}
    >
      {options.map((option) => {
        let state: WageOptionProps["state"] = "valid";
        if (error) state = "error";
        else if (option.wage < wageBar) state = "warning";

        return (
          <WageOption
            key={option.key}
            state={state}
            option={option}
            look={look}
            selected={selectedKey === option.key}
            onSelect={() => onSelectWageOption(option.key)}
            customValue={customWage}
            customLabel={customLabel}
            onCustomValueChange={(value) => {
              if (value === customWage) return;
              setCustomWage(value);
              setTyped(true);
              if (!commitOnBlur) React.startTransition(() => onValueChange({ key: "custom", wage: value ?? 0 }));
            }}
            onCustomCommit={
              commitOnBlur
                ? () => {
                    if (!typed) return;
                    setTyped(false);
                    if (customWage) trackPick({ key: "custom", wage: customWage });
                    onValueChange({ key: "custom", wage: customWage ?? 0 });
                  }
                : undefined
            }
            onCustomTyped={
              commitOnBlur
                ? undefined
                : () => {
                    if (!typed) return;
                    setTyped(false);
                    if (customWage) trackPick({ key: "custom", wage: customWage });
                  }
            }
          />
        );
      })}
      <Message helperText={helperText} error={error} warning={warning} look={look} />
    </ToggleGroup>
  );
}

type MessageProps = Pick<WagePickerProps, "helperText" | "error"> & { warning?: string; look: Look };
function Message({ helperText, error, warning, look }: Readonly<MessageProps>) {
  // A segmented group wraps: the message takes a line of its own under the options.
  const line = look === "segmented" ? "basis-full px-1" : "col-span-full";
  if (error) return <p className={cn(line, "text-small text-error")}>{error}</p>;
  if (warning)
    return (
      <Alert severity="warning" size="small" className={cn(line, "text-extraSmall")}>
        {warning}
      </Alert>
    );
  if (helperText) return <p className={cn(line, "text-small")}>{helperText}</p>;
}

type WageOptionProps = {
  option: WageOption;
  look: Look;
  selected: boolean;
  onSelect: () => void;
  state: "warning" | "error" | "valid";
  customValue?: number;
  customLabel?: string;
  onCustomValueChange: (value: number | undefined) => void;
  onCustomCommit?: () => void;
  onCustomTyped?: () => void;
};

function WageOption({
  option,
  look,
  selected,
  onSelect,
  state,
  customValue,
  customLabel,
  onCustomValueChange,
  onCustomCommit,
  onCustomTyped,
}: Readonly<WageOptionProps>) {
  // The custom wage's field sits beside its choice, never inside it: a field inside a radio is a control in a control.
  if (option.key === "custom")
    return (
      <div
        data-testid={option.key}
        data-validation={state}
        data-state={selected ? "on" : "off"}
        className={optionVariants({ look, custom: true })}
      >
        <MaskedInput
          {...currencyMaskProps}
          className={cn(
            "border-none [&>input]:px-1 [&>input]:py-1.5 [&>input]:text-center sm:[&>input]:px-2",
            look === "segmented" &&
              "w-20 bg-transparent text-inherit [&>input]:py-0 [&>input]:font-semibold [&>input]:text-inherit",
          )}
          placeholder="$22.00 /hr"
          aria-label={customLabel}
          value={String(customValue ?? "")}
          onAccept={(value) => onCustomValueChange(value ? Number(value) : undefined)}
          // A click chooses Custom, unless it commits on leaving; passing the field by keyboard keeps the wage.
          onClick={onCustomCommit ? undefined : onSelect}
          onBlur={onCustomCommit ?? onCustomTyped}
          onKeyDown={(event) => {
            if (event.key === "Enter") (onCustomCommit ?? onCustomTyped)?.();
          }}
        />
        <ToggleGroupItemUnstyled
          value={option.key}
          aria-label={wageOptionLabelLookup.custom}
          className="flex items-center self-stretch"
        >
          <Edit className={cn("size-4 shrink-0", look === "segmented" ? "size-3" : "text-brand")} />
        </ToggleGroupItemUnstyled>
      </div>
    );
  return (
    <ToggleGroupItemUnstyled
      data-testid={option.key}
      data-validation={state}
      value={option.key}
      className={optionVariants({ look, custom: false })}
    >
      <p className={cn("font-semibold", look === "segmented" && "text-normal text-inherit")}>
        {formatCurrency(option.wage)}
      </p>
      <span className={cn("text-small", look === "segmented" ? "font-regular" : "tracking-tighter sm:tracking-normal")}>
        {look === "segmented" && option.key === "avg" ? "Market avg" : wageOptionLabelLookup[option.key]}
      </span>
    </ToggleGroupItemUnstyled>
  );
}
